import {
  Target, Users, Share2, Search, Code2, Palette,
  Film, MessageCircle, Megaphone, Tv, CalendarDays, Box, Globe,
} from "lucide-react";

const SERVICES = [
  { name: "Performance Marketing", icon: Target },
  { name: "Lead Generation", icon: Users },
  { name: "Social Media Marketing", icon: Share2 },
  { name: "SEO", icon: Search },
  { name: "Website Development", icon: Code2 },
  { name: "Branding & Creative", icon: Palette },
  { name: "Video Production", icon: Film },
  { name: "3D Walkthroughs & Models", icon: Box },
  { name: "WhatsApp Marketing", icon: MessageCircle },
  { name: "PR & Influencer", icon: Megaphone },
  { name: "Outdoor Advertising", icon: Tv },
  { name: "Event Marketing", icon: CalendarDays },
  { name: "360° Marketing", icon: Globe },
];

export default function ServicesFlow() {
  // Positions on a circle as percentages of the square container.
  // 42% radius keeps nodes inside while leaving room for the central hub (~24% wide).
  const RADIUS_PCT = 42;
  const positions = SERVICES.map((_, i) => {
    const angle = (i / SERVICES.length) * Math.PI * 2 - Math.PI / 2;
    return {
      x: Math.cos(angle) * RADIUS_PCT,
      y: Math.sin(angle) * RADIUS_PCT,
    };
  });

  return (
    <section
      id="engine"
      data-testid="services-flow-section"
      className="relative py-24 sm:py-32 border-t border-white/5 overflow-hidden"
    >
      {/* Background grid */}
      <div className="absolute inset-0 bg-grid bg-grid-mask opacity-30" />
      <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-[#d4ff4f]/10 blur-[160px]" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="text-center mb-10 sm:mb-16">
          <div className="font-mono text-xs text-[#d4ff4f] mb-3 tracking-[0.2em]">/ Marketing Engine</div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            One agency.<br />
            <span className="italic text-[#d4ff4f]">Thirteen growth levers.</span>
          </h2>
          <p className="max-w-2xl mx-auto text-white/60 mt-6 leading-relaxed">
            Every channel a builder needs to dominate their micro-market — designed, executed and optimised by one in-house team.
          </p>
        </div>

        {/* Hub-and-spoke orbital flow */}
        <div className="relative mx-auto" style={{ width: "min(640px, 95vw)", aspectRatio: "1/1" }}>
          {/* Orbit rings */}
          <div className="absolute inset-[10%] rounded-full border border-[#d4ff4f]/15 animate-[spin_60s_linear_infinite]">
            <span className="absolute -top-1 left-1/2 w-2 h-2 rounded-full bg-[#d4ff4f]" />
          </div>
          <div className="absolute inset-[22%] rounded-full border border-[#d4ff4f]/10 animate-[spin_40s_linear_infinite_reverse]" />
          <div className="absolute inset-[34%] rounded-full border border-[#d4ff4f]/10" />

          {/* SVG lines connecting hub to each node */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="-50 -50 100 100" preserveAspectRatio="none">
            {positions.map((p, i) => (
              <line
                key={i}
                x1={0}
                y1={0}
                x2={p.x}
                y2={p.y}
                stroke="#d4ff4f"
                strokeOpacity="0.18"
                strokeWidth="0.2"
                strokeDasharray="0.6 1"
                vectorEffect="non-scaling-stroke"
              >
                <animate attributeName="stroke-opacity" values="0.05;0.35;0.05" dur="3s" begin={`${i * 0.2}s`} repeatCount="indefinite" />
              </line>
            ))}
          </svg>

          {/* Center hub */}
          <div data-testid="services-flow-hub" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 sm:w-36 sm:h-36 lg:w-40 lg:h-40 rounded-full bg-gradient-to-br from-[#d4ff4f] to-emerald-400 flex flex-col items-center justify-center shadow-[0_0_60px_rgba(212,255,79,0.5)] pulse-ring z-10">
            <Globe className="w-4 h-4 sm:w-7 sm:h-7 lg:w-9 lg:h-9 text-black" />
            <span className="font-display text-[9px] sm:text-sm lg:text-base text-black tracking-tight text-center px-1 mt-0.5 sm:mt-1.5 leading-tight">
              360°<br />Marketing
            </span>
          </div>

          {/* Service nodes */}
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const isHub = s.name === "360° Marketing";
            if (isHub) return null;
            return (
              <div
                key={i}
                data-testid={`service-node-${i}`}
                className="absolute group"
                style={{
                  left: `calc(50% + ${positions[i].x}%)`,
                  top: `calc(50% + ${positions[i].y}%)`,
                  transform: `translate(-50%, -50%)`,
                  animation: `floatY 4s ease-in-out ${i * 0.25}s infinite`,
                }}
              >
                <div className="flex flex-col items-center gap-1">
                  <div className="w-9 h-9 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl border border-[#d4ff4f]/30 bg-[#0a0b08]/90 backdrop-blur flex items-center justify-center text-[#d4ff4f] group-hover:bg-[#d4ff4f] group-hover:text-black transition shadow-lg shadow-black/40">
                    <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
                  </div>
                  <span className="font-mono text-[8px] sm:text-[10px] uppercase tracking-wider text-white/70 text-center max-w-[64px] sm:max-w-[110px] leading-tight">
                    {s.name}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile-only compact hub badge above the chip grid */}
        <div className="hidden flex items-center justify-center mt-2 mb-6" data-testid="services-flow-mobile-hub">
          <div className="relative w-28 h-28 rounded-full bg-gradient-to-br from-[#d4ff4f] to-emerald-400 flex flex-col items-center justify-center shadow-[0_0_40px_rgba(212,255,79,0.4)] pulse-ring">
            <Globe className="w-6 h-6 text-black" />
            <span className="font-display text-xs text-black tracking-tight text-center px-2 mt-1 leading-tight">
              360°<br />Marketing
            </span>
          </div>
        </div>

        {/* Chip grid (always visible — primary view on mobile, complement on desktop) */}
        <div className="mt-4 sm:mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="services-flow-list">
          {SERVICES.filter((s) => s.name !== "360° Marketing").map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#d4ff4f]/40 hover:bg-[#d4ff4f]/[0.04] transition"
              >
                <Icon className="w-4 h-4 text-[#d4ff4f] shrink-0" />
                <span className="text-sm text-white/85">{s.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
